import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FeatherEdit2,
  FeatherFileText,
  FeatherUsers,
  FeatherGrid,
  FeatherCalendar,
  FeatherClock,
  FeatherUser,
  FeatherTag,
  FeatherRefreshCw,
  FeatherFlag,
  FeatherFolder
} from '../ui/icons';
import TechStackCompaniesDual from './TechStackCompaniesDual';
import './NexGateDetail.css';

const NexGateDetail = ({ engagement, onEdit, onBack }) => {
  const techStackRef = useRef();
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Remove scroll position management - we'll handle loading locally
  
  // Debug: Log engagement data to understand structure
  console.log('NexGate Detail - Engagement Data:', engagement);
  console.log('Client Contact Info:', engagement?.client_contact_info);
  
  if (!engagement) {
    return (
      <div className="nexgate-detail">
        <div className="nexgate-detail-error-state">
          <h3>Engagement not found</h3>
          <p>The requested engagement could not be loaded.</p>
          <Button onClick={onBack}>Back to List</Button>
        </div>
      </div>
    );
  }

  const engagementTypes = [
    { value: 'client_augmentation', label: 'Client Augmentation' },
    { value: 'internal_project', label: 'Internal Project' },
    { value: 'custom_software', label: 'Custom Software' }
  ];

  const statuses = [
    { value: 'new', label: 'New' },
    { value: 'in_review', label: 'In Review' },
    { value: 'fulfilled', label: 'Fulfilled' },
    { value: 'archived', label: 'Archived' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'in_review': return 'yellow';
      case 'fulfilled': return 'green';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'red';
      default: return 'gray';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'client_augmentation': return <FeatherUsers size={20} />;
      case 'internal_project': return <FeatherGrid size={20} />;
      case 'custom_software': return <FeatherFileText size={20} />;
      default: return <FeatherFileText size={20} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="nexgate-detail">
      {/* Hero Section */}
      <div className="nexgate-detail-hero-section">
        <div className="nexgate-detail-hero-background">
          <div className="nexgate-detail-hero-pattern"></div>
        </div>
        <div className="nexgate-detail-hero-content">
          <div className="nexgate-detail-hero-left">
            <div className="nexgate-detail-engagement-badge">
              {getTypeIcon(engagement.engagement_type)}
              <Badge variant="outline" className="nexgate-detail-type-badge">
                {engagementTypes.find(t => t.value === engagement.engagement_type)?.label}
              </Badge>
            </div>
            <h1 className="nexgate-detail-hero-title">{engagement.title}</h1>
            <div className="nexgate-detail-hero-meta">
              <div className="nexgate-detail-meta-item">
                <span 
                  className="nexgate-detail-nexgate-id"
                  onClick={() => {
                    navigator.clipboard.writeText(engagement.nexgate_id);
                  }}
                  title="Click to copy ID"
                >
                  {engagement.nexgate_id}
                </span>
              </div>
              <div className="nexgate-detail-meta-item">
                <span className="nexgate-detail-created-info">
                  Created by {engagement.created_by_user?.display_name || 'Unknown'} on {formatDate(engagement.created_at)}
                </span>
              </div>
            </div>
          </div>
          <div className="nexgate-detail-hero-right">
            <div className="nexgate-detail-status-card">
              <div className="nexgate-detail-status-header">
                <span className="nexgate-detail-priority-label">Status</span>
                <Badge variant={getStatusColor(engagement.status)} className="nexgate-detail-status-badge">
                  {statuses.find(s => s.value === engagement.status)?.label}
                </Badge>
              </div>
              <div className="nexgate-detail-priority-indicator">
                <span className="nexgate-detail-priority-label">Priority</span>
                <Badge variant={getPriorityColor(engagement.priority)} className="nexgate-detail-priority-badge">
                  {priorities.find(p => p.value === engagement.priority)?.label}
                </Badge>
              </div>
            </div>
            <Button onClick={onEdit} className="nexgate-detail-edit-button">
              <FeatherEdit2 size={16} />
              Edit Engagement
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="nexgate-detail-content">
        <div className="nexgate-detail-content-grid">
          {/* Left Column */}
          <div className="nexgate-detail-left-column">
            {/* Quick Stats */}
            <div className="nexgate-detail-stats-section">
              <div className="nexgate-detail-stat-card">
                <div className="nexgate-detail-stat-icon">
                  <FeatherUsers size={20} />
                </div>
                <div className="nexgate-detail-stat-content">
                  <span className="nexgate-detail-stat-value">{engagement.required_roles?.length || 0}</span>
                  <span className="nexgate-detail-stat-label">Required Roles</span>
                </div>
              </div>
              <div className="nexgate-detail-stat-card">
                <div className="nexgate-detail-stat-icon">
                  <FeatherCalendar size={20} />
                </div>
                <div className="nexgate-detail-stat-content">
                  <span className="nexgate-detail-stat-value">{engagement.duration}</span>
                  <span className="nexgate-detail-stat-label">Duration</span>
                </div>
              </div>
              <div className="nexgate-detail-stat-card">
                <div className="nexgate-detail-stat-icon">
                  <FeatherClock size={20} />
                </div>
                <div className="nexgate-detail-stat-content">
                  <span className="nexgate-detail-stat-value">{engagement.experience_level}</span>
                  <span className="nexgate-detail-stat-label">Experience Level</span>
                </div>
              </div>
            </div>

            {/* Required Roles */}
            <div className="nexgate-detail-section">
              <h3 className="nexgate-detail-section-title">
                <FeatherUsers size={20} />
                Required Roles
              </h3>
              <div className="nexgate-detail-roles-grid">
                {engagement.required_roles?.map(role => (
                  <div key={role} className="nexgate-detail-role-card">
                    <div className="nexgate-detail-role-icon">
                      <FeatherUser size={16} />
                    </div>
                    <span className="nexgate-detail-role-name">{role}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement Details */}
            <div className="nexgate-detail-section">
              <h3 className="nexgate-detail-section-title">
                <FeatherFileText size={20} />
                Engagement Details
              </h3>
              <div className="nexgate-detail-info-cards">
                <div className="nexgate-detail-info-card">
                  <div className="nexgate-detail-info-icon">
                    <FeatherUser size={16} />
                  </div>
                  <div className="nexgate-detail-info-content">
                    <span className="nexgate-detail-info-label">Requester</span>
                    <span className="nexgate-detail-info-value">{engagement.requester}</span>
                  </div>
                </div>
                <div className="nexgate-detail-info-card">
                  <div className="nexgate-detail-info-icon">
                    <FeatherCalendar size={16} />
                  </div>
                  <div className="nexgate-detail-info-content">
                    <span className="nexgate-detail-info-label">Start Date</span>
                    <span className="nexgate-detail-info-value">{formatDate(engagement.start_date)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {engagement.description && engagement.description.trim() !== '' && (
              <div className="nexgate-detail-section">
                <h3 className="nexgate-detail-section-title">
                  <FeatherFileText size={20} />
                  Description
                </h3>
                <div className="nexgate-detail-notes-card">
                  <p className="nexgate-detail-notes-text">{engagement.description}</p>
                </div>
              </div>
            )}

            {/* Notes */}
            {engagement.notes && engagement.notes.trim() !== '' && (
              <div className="nexgate-detail-section">
                <h3 className="nexgate-detail-section-title">
                  <FeatherFileText size={20} />
                  Notes
                </h3>
                <div className="nexgate-detail-notes-card">
                  <p className="nexgate-detail-notes-text">{engagement.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="nexgate-detail-right-column">
            {/* Project Information - Only show for Custom Software and Internal Project types */}
            {(engagement.engagement_type === 'custom_software' || engagement.engagement_type === 'internal_project') && (
              <div className="nexgate-detail-section">
                <h3 className="nexgate-detail-section-title">
                  <FeatherGrid size={20} />
                  Project Information
                </h3>
                <div className="nexgate-detail-augmentation-compact">
                  <div className="nexgate-detail-augmentation-row">
                    <div className="nexgate-detail-augmentation-item">
                      <div className="nexgate-detail-item-icon">
                        <FeatherFileText size={16} />
                      </div>
                      <div className="nexgate-detail-item-content">
                        <span className="nexgate-detail-item-label">Description</span>
                        <span className="nexgate-detail-item-value">{engagement.project_description || 'Not specified'}</span>
                      </div>
                    </div>
                    <div className="nexgate-detail-augmentation-item">
                      <div className="nexgate-detail-item-icon">
                        <FeatherFlag size={16} />
                      </div>
                      <div className="nexgate-detail-item-content">
                        <span className="nexgate-detail-item-label">Goals</span>
                        <span className="nexgate-detail-item-value">{engagement.project_goals || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="nexgate-detail-augmentation-row">
                    <div className="nexgate-detail-augmentation-item">
                      <div className="nexgate-detail-item-icon">
                        <FeatherFolder size={16} />
                      </div>
                      <div className="nexgate-detail-item-content">
                        <span className="nexgate-detail-item-label">Scope</span>
                        <span className="nexgate-detail-item-value">{engagement.project_scope || 'Not specified'}</span>
                      </div>
                    </div>
                    <div className="nexgate-detail-augmentation-item">
                      <div className="nexgate-detail-item-icon">
                        <FeatherCalendar size={16} />
                      </div>
                      <div className="nexgate-detail-item-content">
                        <span className="nexgate-detail-item-label">Timeline</span>
                        <span className="nexgate-detail-item-value">{engagement.project_timeline || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Augmentation Details */}
            <div className="nexgate-detail-section">
              <h3 className="nexgate-detail-section-title">
                <FeatherUsers size={20} />
                Augmentation Details
              </h3>
              <div className="nexgate-detail-augmentation-compact">
                <div className="nexgate-detail-augmentation-row">
                  <div className="nexgate-detail-augmentation-item">
                    <div className="nexgate-detail-item-icon">
                      <FeatherUsers size={16} />
                    </div>
                    <div className="nexgate-detail-item-content">
                      <span className="nexgate-detail-item-label">Resources</span>
                      <span className="nexgate-detail-item-value">{engagement.number_of_resources}</span>
                    </div>
                  </div>
                  <div className="nexgate-detail-augmentation-item">
                    <div className="nexgate-detail-item-icon">
                      <FeatherTag size={16} />
                    </div>
                    <div className="nexgate-detail-item-content">
                      <span className="nexgate-detail-item-label">Tech Stack</span>
                      <div className="nexgate-detail-tech-stack-compact">
                        {engagement.tech_stack?.slice(0, 3).map(tech => (
                          <Badge key={tech} variant="outline" className="nexgate-detail-tech-badge-compact">
                            {tech}
                          </Badge>
                        ))}
                        {engagement.tech_stack?.length > 3 && (
                          <span className="nexgate-detail-more-tech">+{engagement.tech_stack.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            {engagement.client_contact_info && (
              engagement.client_contact_info.name || 
              engagement.client_contact_info.email || 
              engagement.client_contact_info.phone
            ) && (
              <div className="nexgate-detail-contact-compact">
                <div className="nexgate-detail-contact-header">
                  <FeatherUser size={16} />
                  Client Contact
                </div>
                <div className="nexgate-detail-contact-details">
                  {engagement.client_contact_info.name && (
                    <span className="nexgate-detail-contact-name">{engagement.client_contact_info.name}</span>
                  )}
                  {engagement.client_contact_info.email && (
                    <span className="nexgate-detail-contact-email">{engagement.client_contact_info.email}</span>
                  )}
                  {engagement.client_contact_info.phone && (
                    <span className="nexgate-detail-contact-phone">{engagement.client_contact_info.phone}</span>
                  )}
                </div>
              </div>
            )}

            {/* Attachments */}
            {engagement.attachments?.length > 0 && (
              <div className="nexgate-detail-section">
                <h3 className="nexgate-detail-section-title">
                  <FeatherFileText size={20} />
                  Attachments
                </h3>
                <div className="nexgate-detail-attachments-grid">
                  {engagement.attachments.map(attachment => (
                    <div key={attachment.id} className="nexgate-detail-attachment-card">
                      <div className="nexgate-detail-attachment-icon">
                        <FeatherFileText size={20} />
                      </div>
                      <div className="nexgate-detail-attachment-info">
                        <span className="nexgate-detail-file-name">{attachment.file_name}</span>
                        <span className="nexgate-detail-file-size">
                          {(attachment.file_size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matches */}
            {engagement.matches?.length > 0 && (
              <div className="nexgate-detail-section">
                <h3 className="nexgate-detail-section-title">
                  <FeatherUsers size={20} />
                  Resource Matches
                </h3>
                <div className="nexgate-detail-matches-grid">
                  {engagement.matches.slice(0, 3).map(match => (
                    <div key={match.id} className="nexgate-detail-match-card">
                      <div className="nexgate-detail-match-header">
                        <span className="nexgate-detail-resource-name">{match.resource_name}</span>
                        <span className="nexgate-detail-match-score">{match.match_score}% match</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Section - Tech Stack Companies */}
      <div className="nexgate-detail-bottom-section">
        <div className="nexgate-detail-bottom-section-header">
          <div className="header-content">
            <h2 className="nexgate-detail-section-title">
              <FeatherTag size={24} />
              Tech Stack Companies
            </h2>
            <p className="section-subtitle">
              Companies that match the required tech stack for this engagement
            </p>
          </div>
          <div className="nexgate-detail-header-actions">
            <div className="nexgate-detail-tech-stack-badges">
              {engagement.tech_stack?.map(tech => (
                <Badge key={tech} variant="outline" className="tech-badge">
                  {tech}
                </Badge>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={isRefreshing}
              onClick={async () => {
                if (techStackRef.current && techStackRef.current.handleRefresh) {
                  setIsRefreshing(true);
                  try {
                    await techStackRef.current.handleRefresh();
                  } finally {
                    setIsRefreshing(false);
                  }
                }
              }}
            >
              <FeatherRefreshCw size={16} className={isRefreshing ? 'spinning' : ''} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
        
        <TechStackCompaniesDual 
          ref={techStackRef}
          techStack={engagement.tech_stack}
          engagementType={engagement.engagement_type}
          hideHeader={true}
        />
      </div>
    </div>
  );
};

export default NexGateDetail; 